import {
  type SubmissionResult,
  getFormProps,
  getInputProps,
  useForm,
} from "@conform-to/react";
import { CloseButton } from "@headlessui/react";
import { Trans, t } from "@lingui/macro";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { DialogActions } from "@winamp/ui/components/Dialog/DialogActions.js";
import { DialogBody } from "@winamp/ui/components/Dialog/DialogBody.js";
import { DialogDescription } from "@winamp/ui/components/Dialog/DialogDescription.js";
import { DialogTitle } from "@winamp/ui/components/Dialog/DialogTitle.js";
import { DialogWrapper } from "@winamp/ui/components/Dialog/DialogWrapper.js";
import { ErrorMessages } from "@winamp/ui/components/Fieldset/ErrorMessages.js";
import { FormErrorsMessage } from "@winamp/ui/components/Fieldset/FormErrorsMessage.js";
import { getValibotConstraint, parseWithValibot } from "conform-to-valibot";
import { useEffect, useState } from "react";
import { Form, useFetcher } from "react-router";
import { $path } from "safe-routes";
import * as v from "valibot";
import { useIsSubmitting } from "~/hooks/use-is-submitting";
import type { Permission } from "~/models/permission";
import { patchForm } from "~/modules/conform/helpers";
import { Retryable } from "~/modules/retry/Retryable";
import type { SerializeFrom } from "~/modules/routing/types";
import { ReleaseCandidatesSelection } from "~/routes/_app+/distribution+/components/ReleaseCandidatesSelection";
import type { loader as DistributionCreateLoader } from "~/routes/_app+/distribution+/create";
import { getDistributionCreateSchema } from "~/routes/_app+/distribution+/new";
import {
  type DialogProps,
  DialogRetryPlaceholder,
  useDialogDataFetcher,
} from "..";
import { useRouteDialogParams } from "../../RouteDialogsContext";

export const key = "distribution.create";

export const permissions: Array<Permission | RegExp> = [];

export const Schema = () =>
  v.object({
    artistUid: v.pipe(
      v.string(t`The artist ID is required.`),
      v.minLength(1, t`The artist ID is required.`),
    ),
    labelUid: v.pipe(
      v.string(t`The label ID is required.`),
      v.minLength(1, t`The label ID is required.`),
    ),
  });

export default function View({ hashParams, ...props }: DialogProps) {
  const params = useRouteDialogParams(Schema(), hashParams, props.open);

  const dataFetcher = useFetcher<typeof DistributionCreateLoader>({
    key: `${key}-loader`,
  });

  const artistUid = params?.artistUid;
  const labelUid = params?.labelUid;

  useEffect(() => {
    if (props.open && artistUid && labelUid) {
      dataFetcher.load(
        $path("/labels/:labelId/artists/:artistId/distribution/create", {
          artistId: artistUid,
          labelId: labelUid,
        }),
      );
    }
  }, [props.open, artistUid, labelUid]);

  const {
    data,
    isLoading: isFetcherLoading,
    load,
    state,
  } = useDialogDataFetcher<typeof DistributionCreateLoader>(
    artistUid &&
      labelUid &&
      $path("/labels/:labelId/artists/:artistId/distribution/create", {
        artistId: artistUid,
        labelId: labelUid,
      }),
    {
      key,
      deps: [props.open, artistUid, labelUid],
      when: props.open && !!artistUid && !!labelUid,
    },
  );

  const isLoading = isFetcherLoading;

  return (
    <ViewBody
      data={data}
      hashParams={hashParams}
      params={params}
      loading={isLoading}
      loadData={() => load()}
      reloading={state !== "idle"}
      {...props}
    />
  );
}

export function ViewBody({
  data,
  hashParams,
  params,
  loadData,
  reloading,
  ...props
}: {
  data?: SerializeFrom<typeof DistributionCreateLoader>;
  params: v.InferOutput<ReturnType<typeof Schema>> | undefined;
} & DialogProps<true>) {
  const [lastResult, setLastResult] = useState<
    SubmissionResult<Array<string>> | null | undefined
  >(null);

  const formAction = $path(
    "/labels/:labelId/artists/:artistId/distribution/new",
    {
      artistId: params?.artistUid ?? "",
      labelId: params?.labelUid ?? "",
    },
  );
  const isPending = useIsSubmitting({ formAction, formMethod: "GET" });

  const DistributionCreateSchema = getDistributionCreateSchema();
  const [form, fields] = useForm({
    lastResult,
    constraint: getValibotConstraint(DistributionCreateSchema),
    defaultValue: {
      audioOrCollectionUid: "",
      type: "",
    },
    onValidate: ({ formData }) => {
      const validation = parseWithValibot(formData, {
        schema: DistributionCreateSchema,
      });

      return validation;
    },
  });

  const close = () => {
    if (!isPending) {
      closeImmediately();
    }
  };

  const closeImmediately = () => {
    props.onClose(false);
    setLastResult({ initialValue: null });
  };

  const candidates = data?.candidates ?? [];

  return (
    <DialogWrapper {...props} onClose={close} size="xl">
      <DialogTitle>
        <Trans>Distribute a track or a collection</Trans>
      </DialogTitle>
      <DialogDescription>
        <Trans>
          Select a track or an album you want to distribute or go to Media to
          add/manage tracks and/or collections.
        </Trans>
      </DialogDescription>
      <Retryable
        condition={!!data?.candidates}
        fallback={({ shouldRetry, ...retryProps }) => (
          <DialogRetryPlaceholder dialogKey={key} {...retryProps} />
        )}
        onRetry={() => loadData?.()}
        isRetrying={reloading}
      >
        <DialogBody>
          <Form
            action={formAction}
            method="get"
            {...getFormProps(form)}
            className="flex flex-col gap-8"
          >
            <input
              {...getInputProps(fields.type, { type: "hidden" })}
              key={fields.type.key}
            />
            {candidates.length === 0 && (
              <div className="flex flex-col gap-6 ">
                <span className="text-secondary text-sm text-center">
                  <Trans>
                    You gotta whip up a track in your media first to distribute
                    it from here.
                  </Trans>
                </span>
              </div>
            )}
            {candidates.length > 0 && (
              <ReleaseCandidatesSelection
                placeholder={t`Search for collection or track titles`}
                candidates={candidates}
                meta={fields.audioOrCollectionUid}
                onChange={(value) => {
                  const selectedReleaseCandidate = candidates.find(
                    (candidate) =>
                      candidate.audioUid === value ||
                      candidate.collectionUid === value,
                  );

                  patchForm(form, {
                    type:
                      selectedReleaseCandidate?.collectionUid === value
                        ? "collection"
                        : "audio",
                  });
                }}
              />
            )}
            <ErrorMessages errors={fields.audioOrCollectionUid.errors} />
          </Form>
          <FormErrorsMessage errors={form.errors} />
        </DialogBody>
        <DialogActions>
          <CloseButton
            as={Button}
            variant="ghost"
            onClick={close}
            disabled={isPending}
          >
            <Trans>Cancel</Trans>
          </CloseButton>
          {candidates.length > 0 ? (
            <Button form={form.id} type="submit" autoFocus loading={isPending}>
              <Trans>Create release</Trans>
            </Button>
          ) : (
            <Button
              to={$path("/labels/:labelId/artists/:artistId/media", {
                artistId: params?.artistUid ?? "",
                labelId: params?.labelUid ?? "",
              })}
            >
              <Trans>Go to Media</Trans>
            </Button>
          )}
        </DialogActions>
      </Retryable>
    </DialogWrapper>
  );
}
